import { ApplicationPaths } from '@n2-common/applicationPages/ApplicationFormsHelpers';
import { safeRedirect } from '@n2-common/applicationPages/applicationStatusHelpers';
import { assertDataIsNotResponse } from '@n2-common/asserts';
import { getApplicationIds } from '@n2-common/components/ApplicationContext';
import { ApplicationStatusModel } from '@n2-common/serverGeneratedTypings/applicationStatusModel';
import { N2ApplicationStatus } from '@n2-common/serverGeneratedTypings/n2ApplicationStatus';
import { getApplicationStatus } from '@n2-common/services/legacyApplicationApiService';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';
import { generatePath, LoaderFunctionArgs, redirect, useLoaderData } from 'react-router-dom';
import { type TFunction } from 'i18next';
import { getI18n } from 'react-i18next';

type StatusPageStatus = N2ApplicationStatus | 'SignCompleted' | 'SetPinCompleted';

export function getStatusPageLoader(status: N2ApplicationStatus) {
    const { getFixedT } = getI18n();
    const t = getFixedT(null, 'common', 'ApplicationLandingPages');
    const predicate: (applicationStatus: ApplicationStatusModel) => boolean = (applicationStatus) => {
        return applicationStatus.Status == status;
    };
    return async function StatusPageLoader({ request }: LoaderFunctionArgs) {
        const { applicantId, applicationId } = await getApplicationIds(request);

        if (applicantId === null || applicationId === null) {
            return redirect(generatePath(ApplicationPaths.error));
        }

        try {
            const applicationStatus = await getApplicationStatus(applicationId, applicantId, request.signal);

            if (!predicate(applicationStatus)) {
                return safeRedirect(request.url, applicationStatus, applicantId);
            }
            return getStatusPageData(status, t);
        } catch (err) {
            logErrorToServerside(`Error loading data for '${status}'.`, applicationId, applicantId, err || {});
            return redirect(generatePath(ApplicationPaths.error));
        }
    };
}

export function getLandingPageLoader(status: StatusPageStatus) {
    return async function StatusPageLoader() {
        const i18n = getI18n();
        if (!i18n.isInitialized) {
            const t = await new Promise<TFunction>((resolve) => {
                i18n.on('initialized', () => resolve(i18n.getFixedT(null, 'common', 'ApplicationLandingPages')));
            });
            return getStatusPageData(status, t);
        }
        return getStatusPageData(status, i18n.getFixedT(null, 'common', 'ApplicationLandingPages'));
    };
}

type StatusPageData = {
    heading: string;
    subheading: string;
    body?: string;
    linkText?: string;
};
export function useStatusPageLoaderData() {
    const data = useLoaderData() as Awaited<StatusPageData>;
    assertDataIsNotResponse(data);
    return data;
}
function getStatusPageData(status: StatusPageStatus, t: TFunction): StatusPageData {
    return {
        heading: t(`${status}.Headline`, ''),
        subheading: t(`${status}.SubHeadline`, ''),
        body: t<string>(`${status}.Body`, ''),
        linkText: t<string>(`${status}.LinkText`, ''),
    };
}
